import { COMPLETED_FILL_PERCENT } from "@memorang/helpers/language-learning";
import MaterialCommunityIcons from "@react-native-vector-icons/material-design-icons/static";
import { Trans } from "react-i18next";
import { Text } from "react-native-paper";
import type { LevelIconName } from "../../../icon";
import { type AppTheme, useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import DonutScore from "../../DonutScore";
import { LevelIcon } from "../../LevelIcon";
import { SessionReportDonutSurfaceHalo } from "./SessionReportDonutSurfaceHalo";

export type SessionReportProficiencyEstimateView = {
	skillId: string | number;
	skillTitle: string;
	currentLevel: string;
	nextLevel?: string;
	progress: number;
	readyToAdvance: boolean;
	icon: LevelIconName;
};

type Props = {
	estimates: SessionReportProficiencyEstimateView[];
};

const RING_RADIUS = 50;
const RING_INNER_RADIUS = 42;
const RING_ICON_SIZE = 36;
const RING_SIZE = RING_RADIUS * 2;
const MEDIUM_BAND_THRESHOLD = 50;
const TRACK_ALPHA_SUFFIX = "4D";

// Duplicated from ConnectedLevels' getBandColor/getTrackColor: kept local to
// avoid a drive-by refactor of that component for this feature.
const getProficiencyBandColor = (progress: number, theme: AppTheme) => {
	if (progress >= COMPLETED_FILL_PERCENT) {
		return theme.palette.progress.high;
	}
	if (progress >= MEDIUM_BAND_THRESHOLD) {
		return theme.palette.progress.medium;
	}
	if (progress > 0) {
		return theme.palette.progress.low;
	}
	return theme.palette.progress.none;
};

const getProficiencyTrackColor = (color: string, theme: AppTheme) =>
	color.startsWith("#") && color.length === 7
		? `${color}${TRACK_ALPHA_SUFFIX}`
		: theme.palette.progress.none;

const ProficiencyRing = ({
	estimate,
	showSurfaceHalo,
}: {
	estimate: SessionReportProficiencyEstimateView;
	showSurfaceHalo: boolean;
}) => {
	const theme = useAppTheme();
	const isComplete = estimate.progress >= COMPLETED_FILL_PERCENT;
	const color = getProficiencyBandColor(estimate.progress, theme);
	const checkSize = Math.round(RING_ICON_SIZE * 0.75);

	return (
		<Box
			position="relative"
			width={RING_SIZE}
			height={RING_SIZE}
			alignItems="center"
			justifyContent="center"
		>
			{showSurfaceHalo ? (
				<SessionReportDonutSurfaceHalo size={RING_SIZE} />
			) : null}
			<DonutScore
				score={estimate.progress}
				distribution={[estimate.progress, 100 - estimate.progress]}
				variant="progress"
				radius={RING_RADIUS}
				innerRadius={RING_INNER_RADIUS}
				customColorScale={[color, getProficiencyTrackColor(color, theme)]}
				customChildren={
					isComplete ? (
						<Box
							width={RING_ICON_SIZE}
							height={RING_ICON_SIZE}
							borderRadius={RING_ICON_SIZE / 2}
							backgroundColor={color}
							alignItems="center"
							justifyContent="center"
							style={{ borderCurve: "continuous" }}
						>
							<MaterialCommunityIcons
								name="check"
								size={checkSize}
								color={theme.colors.white}
							/>
						</Box>
					) : (
						<LevelIcon
							name={estimate.icon}
							size={RING_ICON_SIZE}
							color={color}
						/>
					)
				}
			/>
		</Box>
	);
};

const getProficiencyMessageKey = (
	estimate: SessionReportProficiencyEstimateView,
) => {
	if (estimate.readyToAdvance) {
		return "session.results.summary.proficiencyEstimateReady";
	}
	if (!estimate.nextLevel) {
		return "session.results.summary.proficiencyEstimateHighest";
	}
	return "session.results.summary.proficiencyEstimateKeepPracticing";
};

export const SessionReportProficiencySection = ({ estimates }: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const isSingleEstimate = estimates.length === 1;

	return (
		<Box gap={16} width="100%">
			<Box
				flexDirection="row"
				justifyContent="center"
				alignItems="flex-start"
				gap={isMobile ? 24 : 62}
				width="100%"
				marginTop={isSingleEstimate ? -72 : 0}
			>
				{estimates.map((estimate) => (
					<ProficiencyRing
						key={String(estimate.skillId)}
						estimate={estimate}
						showSurfaceHalo={isSingleEstimate}
					/>
				))}
			</Box>
			{estimates.map((estimate) => (
				<Text
					key={`msg-${estimate.skillId}`}
					variant="bodyMedium"
					style={{ color: theme.colors.onSurface, textAlign: "center" }}
				>
					<Trans
						i18nKey={getProficiencyMessageKey(estimate)}
						values={{
							skill: estimate.skillTitle,
							currentLevel: estimate.currentLevel,
							nextLevel: estimate.nextLevel ?? "",
						}}
						components={{
							bold: (
								<Text
									variant="bodyMedium"
									style={{
										color: theme.colors.onSurface,
										fontWeight: "700",
									}}
								>
									{""}
								</Text>
							),
						}}
					/>
				</Text>
			))}
		</Box>
	);
};
